import type { StatefulObservationViewT } from "@/types/database/observations";
import type { StatefulTrackerViewT } from "@/types/database/trackers";

interface ItemWithStatus {
	id: string;
	name: string;
	is_due: boolean;
	is_locked?: boolean;
	end_date?: string | null;
	is_complete?: boolean;
	type: "tracker" | "observation";
	data: StatefulTrackerViewT | StatefulObservationViewT;
}

interface UseFormsToDoReturn {
	toDo: ItemWithStatus[];
	upcoming: ItemWithStatus[];
	completed: ItemWithStatus[];
	itemCount: number; // Number of items (trackers/observations) that need attention
	taskCount: number; // Total number of tasks (due entries + due observations)
	isUpToDate: boolean;
}

export function useFormsToDo(
	trackers: StatefulTrackerViewT[],
	observations: StatefulObservationViewT[],
): UseFormsToDoReturn {
	// Transform trackers into a common format
	const transformTrackers = (
		trackers: StatefulTrackerViewT[],
	): ItemWithStatus[] => {
		return (
			trackers?.map((tracker) => ({
				id: tracker.tracker_id,
				name: tracker.tracker_name || "",
				is_due: tracker.is_due === true,
				is_locked: tracker.next_entry?.is_locked,
				end_date: tracker.end_date,
				is_complete: tracker.tracker_complete,
				type: "tracker",
				data: tracker,
			})) || []
		);
	};

	// Transform observations into a common format
	const transformObservations = (
		observations: StatefulObservationViewT[],
	): ItemWithStatus[] => {
		if (!observations) return [];

		return observations
			.filter((observation) => observation.roles?.includes("patient"))
			.map((observation) => ({
				id: observation.observation_id,
				name: observation.observation_name || "",
				is_due: observation.is_due === true,
				is_locked: observation.next_entry?.is_locked,
				end_date: observation.end_date,
				is_complete: observation.observation_complete,
				type: "observation",
				data: observation,
			}));
	};

	// Combine and sort all items
	const allItems = [
		...transformTrackers(trackers),
		...transformObservations(observations),
	]
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((item, index) => ({ ...item, index: index + 1 }));

	// Calculate counts
	const { itemCount, taskCount } = allItems.reduce(
		(acc, item) => {
			if (item.type === "tracker") {
				const tracker = item.data as StatefulTrackerViewT;
				const dueEntriesLength = tracker.due_entries?.length || 0;
				if (dueEntriesLength > 0) {
					acc.itemCount += 1;
					acc.taskCount += dueEntriesLength;
				}
			} else {
				const observation = item.data as StatefulObservationViewT;
				if (observation.is_due === true) {
					acc.itemCount += 1;
					acc.taskCount += 1;
				}
			}
			return acc;
		},
		{ itemCount: 0, taskCount: 0 },
	);

	// Filter items into appropriate categories
	const toDo = allItems.filter((item) => {
		if (item.type === "tracker") {
			const tracker = item.data as StatefulTrackerViewT;
			return (tracker.due_entries?.length || 0) > 0;
		}
		const observation = item.data as StatefulObservationViewT;
		return observation.is_due === true;
	});

	const upcoming = allItems.filter(
		(item) => item.is_locked === true && !item.is_due,
	);
	const completed = allItems.filter(
		(item) => item.end_date !== null || item.is_complete === true,
	);

	const isUpToDate = taskCount === 0;

	return {
		toDo,
		upcoming,
		completed,
		itemCount,
		taskCount,
		isUpToDate,
	};
}
